import React, { useState } from 'react';
import {
  Button, FormControl, FormErrorMessage,
  Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Text, Box, VStack, useToast, useColorModeValue,
} from '@chakra-ui/react';
import api from '../api';

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [formValues, setFormValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleColor = useColorModeValue('gray.500', 'gray.400');
  const footerBg = useColorModeValue('gray.50', 'gray.750');

  const handleChange = (field) => (e) => {
    setFormValues(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleClose = () => {
    if (submitting) return;
    setFormValues(EMPTY);
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (formValues.newPassword !== formValues.confirmPassword) {
      setErrors({ confirmPassword: 'Паролі не співпадають.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/change-password/', {
        old_password: formValues.currentPassword,
        new_password: formValues.newPassword,
        confirm_password: formValues.confirmPassword,
      });
      toast({ title: 'Пароль змінено', status: 'success', duration: 4000, isClosable: true });
      setFormValues(EMPTY);
      setErrors({});
      onClose();
    } catch (error) {
      const data = error.response?.data;
      if (data) {
        if (typeof data === 'string') {
          toast({ title: 'Помилка', description: data, status: 'error', duration: 5000, isClosable: true });
        } else {
          setErrors({
            currentPassword: data.old_password?.[0],
            newPassword: data.new_password?.[0],
            confirmPassword: data.confirm_password?.[0] || data.non_field_errors?.[0],
          });
        }
      } else {
        toast({ title: 'Помилка', description: 'Сталася помилка. Спробуйте пізніше.', status: 'error', duration: 5000, isClosable: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    { key: 'currentPassword', label: 'Поточний пароль' },
    { key: 'newPassword',     label: 'Новий пароль' },
    { key: 'confirmPassword', label: 'Підтвердіть новий пароль' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form" onSubmit={handleSubmit}
        bg={cardBg} borderRadius="xl"
        border="1px solid" borderColor={borderColor}
        overflow="hidden" boxShadow="lg"
        position="relative"
      >
        <Box position="absolute" top={0} left={0} right={0} height="3px" bg="linear-gradient(90deg, #16a34a, #15803d)" />

        <ModalHeader pt={5} pb={2} px={5}>
          <Text fontSize="md" fontWeight="bold" color={textColor}>Зміна пароля</Text>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} borderRadius="md" isDisabled={submitting} />

        <ModalBody px={5} pb={4}>
          <VStack spacing={3} align="stretch">
            {fields.map(({ key, label }) => (
              <FormControl key={key} isInvalid={Boolean(errors[key])}>
                <Text fontSize="xs" fontWeight="semibold" color={subtleColor} mb={1}>{label}</Text>
                <Input
                  type="password"
                  value={formValues[key]}
                  onChange={handleChange(key)}
                  required
                  size="sm"
                  borderRadius="lg"
                  borderColor={borderColor}
                  bg={cardBg}
                  _focus={{ borderColor: '#16a34a', boxShadow: '0 0 0 1px rgba(22,163,74,0.3)' }}
                />
                <FormErrorMessage fontSize="xs">{errors[key]}</FormErrorMessage>
              </FormControl>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid" borderColor={borderColor}
          py={3} px={5} bg={footerBg} gap={2}
        >
          <Button
            type="submit"
            isLoading={submitting}
            bg="linear-gradient(135deg, #16a34a, #15803d)"
            color="white" size="sm" px={5} borderRadius="lg"
            boxShadow="0 2px 6px rgba(22,163,74,0.3)"
            _hover={{ boxShadow: '0 4px 10px rgba(22,163,74,0.4)' }}
          >
            Зберегти
          </Button>
          <Button
            variant="outline" size="sm" px={4} borderRadius="lg"
            borderColor={borderColor} color={textColor}
            _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
            onClick={handleClose}
            isDisabled={submitting}
          >
            Скасувати
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangePasswordModal;
